import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoRegistradoService } from '../services';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Package
} from 'lucide-react';
import './MedicamentosRegistrados.css';

const MedicamentosRegistrados = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filtros e busca
  const [filtros, setFiltros] = useState({
    search: '',
    categoria: '',
    tipoControle: '',
    page: 1,
    limit: 20,
    sortBy: 'nome',
    sortOrder: 'ASC'
  });
  const [totalItems, setTotalItems] = useState(0);
  const [categorias, setCategorias] = useState([]);

  // Verificar permissão
  useEffect(() => {
    if (!['admin', 'farmaceutico'].includes(user?.cargo)) {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  // Carregar categorias
  useEffect(() => {
    carregarCategorias();
  }, []);

  // Carregar medicamentos
  useEffect(() => {
    carregarMedicamentos();
  }, [filtros]);

  const carregarCategorias = async () => {
    try {
      const categoriasData = await medicamentoRegistradoService.getCategorias();
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const carregarMedicamentos = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await medicamentoRegistradoService.getAll(filtros);
      setMedicamentos(response.medicamentos || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error('Erro ao carregar medicamentos:', error);
      setError('Erro ao carregar medicamentos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor,
      page: 1 // Reset para primeira página quando filtrar
    }));
  };

  const handleBusca = (e) => {
    e.preventDefault();
    // A busca já é feita automaticamente pelo useEffect
  };

  const handlePageChange = (novaPagina) => {
    setFiltros(prev => ({ ...prev, page: novaPagina }));
  };

  const handleEditarMedicamento = (id) => {
    navigate(`/medicamentos-registrados/editar/${id}`);
  };

  const totalPages = Math.ceil(totalItems / filtros.limit);
  const startItem = (filtros.page - 1) * filtros.limit + 1;
  const endItem = Math.min(filtros.page * filtros.limit, totalItems);

  const formatarCategoria = (categoria) => {
    const categoriaMap = {
      'analgesico': 'Analgésico',
      'antibiotico': 'Antibiótico',
      'antinflamatorio': 'Anti-inflamatório',
      'cardiovascular': 'Cardiovascular',
      'dermatologico': 'Dermatológico',
      'gastrointestinal': 'Gastrointestinal',
      'neurologico': 'Neurológico',
      'psiquiatrico': 'Psiquiátrico',
      'respiratorio': 'Respiratório',
      'vitaminas': 'Vitaminas',
      'outros': 'Outros'
    };
    return categoriaMap[categoria] || categoria;
  };

  const formatarTipoControle = (tipo) => {
    const tipoMap = {
      'sem_controle': 'Sem Controle',
      'normal': 'Tarja Vermelha',
      'especial': 'Tarja Preta'
    };
    return tipoMap[tipo] || tipo;
  };

  return (
    <div className="medicamentos-registrados-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <Package size={32} className="header-icon" />
            <div>
              <h1>Medicamentos Registrados</h1>
              <p>Gerenciar cadastro de medicamentos no sistema</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="content-wrapper">
        {/* Filtros e Busca */}
        <div className="filters-section">
          <div className="filters-row">
            <form onSubmit={handleBusca} className="search-form">
              <div className="search-input-group">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Buscar por nome, princípio ativo ou código de barras..."
                  value={filtros.search}
                  onChange={(e) => handleFiltroChange('search', e.target.value)}
                />
              </div>
            </form>
            
            <div className="filter-groups">
              <div className="filter-group">
                <label>Categoria:</label>
                <select
                  value={filtros.categoria}
                  onChange={(e) => handleFiltroChange('categoria', e.target.value)}
                >
                  <option value="">Todas</option>
                  {categorias.map(categoria => (
                    <option key={categoria} value={categoria}>
                      {formatarCategoria(categoria)}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Tipo de Controle:</label>
                <select
                  value={filtros.tipoControle}
                  onChange={(e) => handleFiltroChange('tipoControle', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="sem_controle">Sem Controle</option>
                  <option value="normal">Tarja Vermelha</option>
                  <option value="especial">Tarja Preta</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Medicamentos */}
        <div className="medicamentos-list">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Carregando medicamentos...</p>
            </div>
          ) : medicamentos.length === 0 ? (
            <div className="empty-state">
              <Package size={64} />
              <h3>Nenhum medicamento encontrado</h3>
              <p>Não há medicamentos registrados que correspondam aos filtros aplicados.</p>
            </div>
          ) : (
            <>
              <div className="results-info">
                <span>
                  Mostrando {startItem} a {endItem} de {totalItems} medicamentos
                </span>
              </div>
              
              <div className="medicamentos-grid">
                {medicamentos.map((medicamento) => (
                  <div 
                    key={medicamento.id} 
                    className="medicamento-card"
                    onClick={() => handleEditarMedicamento(medicamento.id)}
                  >
                    <div className="medicamento-header">
                      <h3>{medicamento.nome}</h3>
                      <div className="medicamento-actions">
                        <button
                          className="action-btn edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditarMedicamento(medicamento.id);
                          }}
                          title="Editar medicamento"
                        >
                          <Edit size={16} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="medicamento-info">
                      <div className="info-row">
                        <span className="label">Princípio Ativo:</span>
                        <span className="value">{medicamento.principioAtivo}</span>
                      </div>
                      
                      {medicamento.dosagem && (
                        <div className="info-row">
                          <span className="label">Dosagem:</span>
                          <span className="value">{medicamento.dosagem}</span>
                        </div>
                      )}
                      
                      <div className="info-row">
                        <span className="label">Categoria:</span>
                        <span className="value">{formatarCategoria(medicamento.categoria)}</span>
                      </div>
                      
                      <div className="info-row">
                        <span className="label">Tipo de Controle:</span>
                        <span className={`value control-type ${medicamento.tipoControle}`}>
                          {formatarTipoControle(medicamento.tipoControle)}
                        </span>
                      </div>
                      
                      {medicamento.codigoBarras && (
                        <div className="info-row">
                          <span className="label">Código de Barras:</span>
                          <span className="value">{medicamento.codigoBarras}</span>
                        </div>
                      )}
                      
                      {medicamento.prescricaoObrigatoria && (
                        <div className="info-row">
                          <span className="label prescricao-obrigatoria">
                            ⚕️ Prescrição Obrigatória
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Paginação */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(filtros.page - 1)}
                    disabled={filtros.page === 1}
                  >
                    <ChevronLeft size={16} />
                    Anterior
                  </button>
                  
                  <div className="pagination-info">
                    <span>Página {filtros.page} de {totalPages}</span>
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => handlePageChange(filtros.page + 1)}
                    disabled={filtros.page === totalPages}
                  >
                    Próxima
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicamentosRegistrados;